import React from 'react';
import { ArrowsLeftRight, DeviceMobile, SignIn, SignOut, Users, WarningCircle } from '@phosphor-icons/react';
import { AnteilRing } from '../../../../components/immobilien/ui';
import { eur } from '../../../../lib/immobilien/format';
import { deDatum } from '../../../../lib/immobilien/objektDetail';
import { BUCHHALTUNG_FOLGT } from '../../../../lib/immobilien/hinweise';
import type { Kennzahlen, Wechsel } from '../../../../lib/mieter/liste';

/** Widgets der Mieterliste (README §5.1, Screens 01/40): Aktive Mieter, Rückstand, Mieterportal, Ein- & Auszüge. */
export function MieterWidgets({ k, wechsel, onAktiv, onRueckstand, onMieter }: {
  k: Kennzahlen;
  wechsel: readonly Wechsel[];
  onAktiv: () => void;
  onRueckstand: () => void;
  onMieter: (id: string) => void;
}) {
  const tastatur = (aktion: () => void) => (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.target !== e.currentTarget) return;
    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); aktion(); }
  };
  const rueckstandBekannt = k.rueckstand !== null;
  return (
    <section aria-label="Übersicht Mieter" className="flex flex-col" style={{ gap: 14 }}>
      <div role="button" tabIndex={0} aria-label="Aktive Mieter anzeigen" onClick={onAktiv} onKeyDown={tastatur(onAktiv)}
        className="immo-akzent relative flex flex-col overflow-hidden cursor-pointer" style={{ minHeight: 150, borderRadius: 26, padding: 18 }}>
        <Users weight="fill" size={150} aria-hidden className="absolute pointer-events-none" style={{ right: -14, bottom: -26, opacity: 0.12 }} />
        <span className="flex items-center" style={{ gap: 6, fontSize: 13, fontWeight: 700 }}><Users weight="fill" size={15} />Aktive Mieter</span>
        <div className="flex items-end" style={{ marginTop: 'auto', gap: 16 }}>
          <span className="immo-zahl" style={{ fontSize: 58, fontWeight: 800, letterSpacing: '-0.04em', lineHeight: 0.95 }}>{k.aktiv}</span>
          <div className="flex flex-col" style={{ gap: 2, paddingBottom: 4 }}>
            <span className="immo-zahl" style={{ fontSize: 13, fontWeight: 700 }}>{eur(k.sollmiete, { nachkomma: 0 })} Sollmiete / Monat</span>
            <span style={{ fontSize: 12, color: 'var(--acc-ink2)' }}>
              in {k.objekte} {k.objekte === 1 ? 'Objekt' : 'Objekten'} · {k.einzuegeGeplant} Einzug geplant
            </span>
          </div>
        </div>
      </div>

      {/* auto-fit mit nur zwei Widgets: leere Spuren fallen weg, beide teilen sich die Breite */}
      <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gridAutoRows: 'minmax(150px, auto)', gap: 14 }}>
        <div
          role={rueckstandBekannt ? 'button' : undefined}
          tabIndex={rueckstandBekannt ? 0 : undefined}
          aria-label={rueckstandBekannt ? 'Mieter im Rückstand anzeigen' : undefined}
          onClick={rueckstandBekannt ? onRueckstand : undefined}
          onKeyDown={rueckstandBekannt ? tastatur(onRueckstand) : undefined}
          className={`immo-glas flex flex-col ${rueckstandBekannt ? 'immo-hover-widget cursor-pointer' : ''}`}
          style={{ borderRadius: 26, padding: 16, gap: 6 }}
        >
          <span className="flex items-center" style={{ gap: 6, fontSize: 13, fontWeight: 600, color: 'var(--bad)' }}><WarningCircle weight="fill" size={15} />Rückstand</span>
          <div className="flex flex-col" style={{ marginTop: 'auto', gap: 2 }}>
            <span className="immo-zahl" style={{ fontSize: 26, fontWeight: 800, letterSpacing: '-0.03em', color: rueckstandBekannt ? 'var(--bad)' : 'var(--ink3)' }}>
              {k.rueckstand === null ? '–' : eur(k.rueckstand, { nachkomma: 0 })}
            </span>
            <span style={{ fontSize: 12, color: 'var(--ink2)' }}>
              {rueckstandBekannt ? `${k.rueckstandMieter} Mieter im Rückstand` : BUCHHALTUNG_FOLGT}
            </span>
          </div>
        </div>

        <div className="immo-glas flex flex-col" style={{ borderRadius: 26, padding: 16, gap: 6 }}>
          <span className="flex items-center" style={{ gap: 6, fontSize: 13, fontWeight: 600, color: 'var(--acc)' }}><DeviceMobile weight="fill" size={15} />Mieterportal</span>
          <div className="flex flex-1 items-center" style={{ gap: 12 }}>
            <AnteilRing prozent={k.portalQuote} />
            <div className="flex flex-col" style={{ gap: 2, fontSize: 12, color: 'var(--ink2)' }}>
              <span><b style={{ color: 'var(--ink)' }}>{k.portalAktiv}</b> aktiv</span>
              <span title="Einladungen werden noch angebunden"><b style={{ color: 'var(--ink)' }}>–</b> eingeladen</span>
            </div>
          </div>
        </div>
      </div>

      <div className="immo-glas flex flex-wrap items-center" style={{ borderRadius: 26, padding: '14px 16px', gap: 14 }}>
        <span className="flex items-center whitespace-nowrap" style={{ gap: 6, fontSize: 13, fontWeight: 600, color: 'var(--acc)' }}><ArrowsLeftRight weight="fill" size={15} />Ein- &amp; Auszüge</span>
        <div className="flex flex-1 flex-wrap min-w-0" style={{ gap: 10 }}>
          {wechsel.length === 0 && <span style={{ fontSize: 13, color: 'var(--ink3)' }}>Keine Ein- oder Auszüge geplant</span>}
          {wechsel.map(w => {
            const einzug = w.art === 'einzug';
            const farbe = einzug ? 'var(--info)' : 'var(--warn)';
            const IconKomp = einzug ? SignIn : SignOut;
            return (
              <button key={`${w.art}-${w.id}`} type="button" onClick={() => onMieter(w.id)}
                className="immo-hover-flaeche flex items-center min-w-0 cursor-pointer text-left"
                style={{ flex: '1 1 220px', gap: 10, padding: '10px 12px', borderRadius: 16, border: 'none', background: 'var(--fill)', color: 'var(--ink)' }}>
                <span className="flex items-center justify-center shrink-0" style={{ width: 36, height: 36, borderRadius: 12, background: einzug ? 'var(--info-soft)' : 'var(--warn-soft)' }}>
                  <IconKomp weight="fill" size={17} color={farbe} />
                </span>
                <span className="flex flex-col flex-1 min-w-0">
                  <span className="truncate" style={{ fontSize: 13, fontWeight: 700 }}>{w.name}</span>
                  <span className="truncate" style={{ fontSize: 11, color: 'var(--ink3)' }}>{einzug ? 'Einzug' : 'Auszug'} · {[w.objektName, w.einheit].filter(Boolean).join(' ')}</span>
                </span>
                <span className="immo-zahl" style={{ fontSize: 12, fontWeight: 800, color: farbe }}>{deDatum(w.datum)}</span>
              </button>
            );
          })}
        </div>
      </div>
    </section>
  );
}
